'use client';

import { useEffect, useState } from 'react';
import { formatDurationMinutes } from '@workspace/booking/format-duration';
import { AppOverlayPortal } from './app-overlay-portal';

export type MiniAppTeacherCourse = {
  id: string;
  slug: string;
  name: string;
  summary: string | null;
  description: string | null;
  imageUrl: string | null;
  durationMinutes: number;
  unitPrice: number;
};

export type MiniAppTeacher = {
  id: string;
  slug: string;
  name: string;
  bio: string | null;
  avatarUrl: string | null;
  photos: Array<{ id: string; url: string }>;
  courses: MiniAppTeacherCourse[];
  venues: Array<{ id: string; name: string }>;
};

function teacherInitial(name: string) {
  return Array.from(name.replace(/老師$/, '').trim() || '師')[0];
}

function formatCurrency(amountInCents: number) {
  return new Intl.NumberFormat('zh-TW', {
    style: 'currency',
    currency: 'TWD',
    maximumFractionDigits: 0,
  }).format(amountInCents / 100);
}

export function TeacherPortrait({ teacher, className = '' }: { teacher: MiniAppTeacher; className?: string }) {
  if (teacher.avatarUrl) {
    return <span className={`teacher-portrait ${className}`}><img src={teacher.avatarUrl} alt={`${teacher.name}頭像`} /></span>;
  }
  return <span className={`teacher-portrait teacher-portrait-fallback ${className}`} aria-hidden="true">{teacherInitial(teacher.name)}</span>;
}

export function BookedTeacherStrip({ teachers, onSelect, onViewAll }: {
  teachers: MiniAppTeacher[];
  onSelect: (teacher: MiniAppTeacher) => void;
  onViewAll: () => void;
}) {
  if (!teachers.length) return null;

  return <section className="home-teachers">
    <header><div><span>MY TEACHERS</span><h2>我的老師</h2></div><button type="button" onClick={onViewAll}>查看全部</button></header>
    <div className="home-teacher-rail">
      {teachers.map((teacher) => <button type="button" key={teacher.id} onClick={() => onSelect(teacher)} aria-label={`查看${teacher.name}介紹`}>
        <TeacherPortrait teacher={teacher}/>
        <span><strong>{teacher.name}</strong><small>{teacher.courses.slice(0, 2).map((course) => course.name).join(' · ') || '查看老師介紹'}</small></span>
        <b aria-hidden="true">›</b>
      </button>)}
    </div>
  </section>;
}

export function TeacherBrowser({ teachers, selectedSlug, onSelect, onBack, onBook }: {
  teachers: MiniAppTeacher[];
  selectedSlug: string | null;
  onSelect: (teacher: MiniAppTeacher) => void;
  onBack: () => void;
  onBook: (teacher: MiniAppTeacher) => void;
}) {
  if (selectedSlug) {
    const selected = teachers.find((teacher) => teacher.slug === selectedSlug) ?? null;
    return selected
      ? <TeacherDetail teacher={selected} onBack={onBack} onBook={() => onBook(selected)}/>
      : <section className="teacher-missing"><div aria-hidden="true">?</div><span>TEACHER PROFILE</span><h2>找不到這位老師</h2><p>這位老師可能已停用，或不屬於目前的商店。</p><button type="button" onClick={onBack}>返回師資列表</button></section>;
  }

  return <section className="teacher-browser">
    <header className="teacher-browser-heading"><span>OUR TEACHERS</span><h2>認識授課老師</h2><p>從專長、授課課程與服務場館，找到適合你的老師。</p></header>
    <div className="teacher-list">
      {teachers.map((teacher) => <button type="button" className="teacher-list-card" key={teacher.id} aria-label={`查看${teacher.name}介紹`} onClick={() => onSelect(teacher)}>
        <TeacherPortrait teacher={teacher}/>
        <span className="teacher-list-copy"><span>TEACHER</span><strong>{teacher.name}</strong><small>{teacher.bio || '查看老師介紹、授課課程與可預約時段。'}</small>{teacher.venues.length > 0 && <span className="teacher-list-venues" aria-label="可授課場館">{teacher.venues.map((venue) => <em key={venue.id}>{venue.name}</em>)}</span>}<span className="teacher-course-tags">{teacher.courses.slice(0, 3).map((course) => <em key={course.id}>{course.name}</em>)}</span></span>
        <b aria-hidden="true">›</b>
      </button>)}
    </div>
    {!teachers.length && <div className="teacher-list-empty"><strong>目前沒有公開師資</strong><p>店家完成老師資料設定後，會顯示在這裡。</p></div>}
  </section>;
}

function TeacherDetail({ teacher, onBack, onBook }: { teacher: MiniAppTeacher; onBack: () => void; onBook: () => void }) {
  const [photoIndex, setPhotoIndex] = useState<number | null>(null);
  const selectedPhoto = photoIndex === null ? null : teacher.photos[photoIndex] ?? null;

  useEffect(() => {
    if (photoIndex === null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPhotoIndex(null);
      if (event.key === 'ArrowLeft') setPhotoIndex((current) => current === null ? null : (current - 1 + teacher.photos.length) % teacher.photos.length);
      if (event.key === 'ArrowRight') setPhotoIndex((current) => current === null ? null : (current + 1) % teacher.photos.length);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [photoIndex, teacher.photos.length]);

  return <section className="teacher-detail">
    <button type="button" className="teacher-detail-back" onClick={onBack}>‹ <span>返回師資列表</span></button>
    <header className="teacher-detail-hero">
      <TeacherPortrait teacher={teacher} className="teacher-detail-portrait"/>
      <div><span>TEACHER PROFILE</span><h2>{teacher.name}</h2><p>{teacher.courses.length ? `教授 ${teacher.courses.length} 門課程` : '授課老師'}</p></div>
    </header>

    <section className="teacher-detail-section teacher-bio"><span>ABOUT</span><h3>老師介紹</h3><p>{teacher.bio || '老師尚未填寫完整介紹，歡迎查看授課課程與可預約時段。'}</p></section>

    {teacher.venues.length > 0 && <section className="teacher-detail-section"><span>LOCATIONS</span><h3>服務場館</h3><div className="teacher-venue-tags">{teacher.venues.map((venue) => <span key={venue.id}>{venue.name}</span>)}</div></section>}

    {teacher.photos.length > 0 && <section className="teacher-detail-section"><span>GALLERY</span><h3>授課照片</h3><div className="teacher-photo-rail">{teacher.photos.map((photo, index) => <button type="button" key={photo.id} onClick={() => setPhotoIndex(index)} aria-label={`放大${teacher.name}授課照片 ${index + 1}`}><img src={photo.url} alt={`${teacher.name}授課照片 ${index + 1}`}/><span>{index + 1}</span></button>)}</div></section>}

    <section className="teacher-detail-section"><span>CLASSES</span><h3>授課課程</h3><div className="teacher-course-list">{teacher.courses.map((course) => <article key={course.id}>
      <div className="teacher-course-image">{course.imageUrl ? <img src={course.imageUrl} alt={`${course.name}課程圖片`}/> : <span aria-hidden="true">{Array.from(course.name)[0]}</span>}</div>
      <div><header><h4>{course.name}</h4><strong>{formatCurrency(course.unitPrice)}</strong></header><p>{course.description || course.summary || '店家尚未填寫課程說明。'}</p><footer><span>{formatDurationMinutes(course.durationMinutes)}</span><em>單堂原價</em></footer></div>
    </article>)}</div>{!teacher.courses.length && <div className="teacher-list-empty"><strong>尚未公開授課課程</strong><p>請稍後再查看老師可預約的課程。</p></div>}</section>

    <div className="teacher-detail-action"><button type="button" onClick={onBook}>查看老師時段</button></div>

    {selectedPhoto && <AppOverlayPortal><div className="teacher-lightbox" role="dialog" aria-modal="true" aria-label={`${teacher.name}照片瀏覽`} onClick={() => setPhotoIndex(null)}>
      <button type="button" className="teacher-lightbox-close" onClick={() => setPhotoIndex(null)} aria-label="關閉照片">×</button>
      {teacher.photos.length > 1 && <button type="button" className="teacher-lightbox-prev" onClick={(event) => { event.stopPropagation(); setPhotoIndex((current) => current === null ? null : (current - 1 + teacher.photos.length) % teacher.photos.length); }} aria-label="上一張照片">‹</button>}
      <img src={selectedPhoto.url} alt={`${teacher.name}授課照片 ${(photoIndex ?? 0) + 1}`} onClick={(event) => event.stopPropagation()}/>
      {teacher.photos.length > 1 && <button type="button" className="teacher-lightbox-next" onClick={(event) => { event.stopPropagation(); setPhotoIndex((current) => current === null ? null : (current + 1) % teacher.photos.length); }} aria-label="下一張照片">›</button>}
      <span>{(photoIndex ?? 0) + 1} / {teacher.photos.length}</span>
    </div></AppOverlayPortal>}
  </section>;
}
